import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { OrganizerPortalSummary } from '@radevents/shared';
import organizerPortalService from '../services/organizerPortalService';
import { useAuth } from './AuthContext';

interface OrganizerPortalState {
  organizers: OrganizerPortalSummary[];
  loading: boolean;
  /** Mindestens ein verknuepfter Veranstalter — steuert Navigation und Routen-Zugang. */
  isOrganizer: boolean;
  reload: () => void;
}

const Ctx = createContext<OrganizerPortalState>({
  organizers: [], loading: true, isOrganizer: false, reload: () => {},
});

/**
 * Mit welchen Veranstaltern das angemeldete Konto verknuepft ist.
 *
 * Eigener Aufruf statt eines Felds in der Anmeldeantwort: die App holt ihren Anmeldezustand beim
 * Start ueber die Token-Erneuerung, und die liefert das rohe Konto-Objekt aus der Datenbank. Eine
 * frisch erteilte Verknuepfung wirkt so ausserdem sofort — ohne neue Anmeldung, und ein Entzug
 * greift, ohne auf den Ablauf des Tokens zu warten.
 */
export const OrganizerPortalProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const [organizers, setOrganizers] = useState<OrganizerPortalSummary[]>([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    // Solange die Anmeldung selbst noch hydriert, ist "nicht angemeldet" keine Auskunft, sondern
    // ein Zwischenstand. Wer hier zu frueh false meldet, laesst RequireOrganizer den Nutzer beim
    // direkten Aufruf der Portal-URL hinauswerfen — genau das passierte beim Neuladen.
    if (authLoading) { setLoading(true); return; }
    if (!isAuthenticated) { setOrganizers([]); setLoading(false); return; }
    setLoading(true);
    organizerPortalService.me()
      .then(setOrganizers)
      .catch(() => setOrganizers([]))
      .finally(() => setLoading(false));
  }, [isAuthenticated, authLoading]);

  useEffect(() => { load(); }, [load]);

  const value = useMemo(
    () => ({ organizers, loading, isOrganizer: organizers.length > 0, reload: load }),
    [organizers, loading, load],
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
};

export const useOrganizerPortal = (): OrganizerPortalState => useContext(Ctx);
